import React from 'react';
import { NavLink as Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "Wie zijn wij?",
    content: (
      <div className="space-y-4">
        <p>
          Verduurzaamwijzer.nu is een onafhankelijk informatie- en adviesplatform voor huiseigenaren die hun woning
          willen verduurzamen. Wij zijn opgericht vanuit de overtuiging dat iedere Nederlander recht heeft op
          eerlijk, helder en onafhankelijk advies over duurzame woningoplossingen — zonder verborgen belangen.
        </p>
        <p>
          Het platform is een initiatief van <strong>De Content Factory BV</strong>, gevestigd in Den Haag. Ons team
          bestaat uit energieadviseurs, redacteuren en technologen die dagelijks werken aan betrouwbare informatie
          over isolatie, warmtepompen, zonnepanelen, thuisbatterijen en ventilatie.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <tbody>
              {[
                ["Organisatie", "De Content Factory BV"],
                ["Handelsnaam", "Verduurzaamwijzer.nu"],
                ["Vestigingsadres", "Oude Middenweg 17, 2491 AC Den Haag"],
                ["KvK-nummer", "84512490"],
                ["E-mail", "contact@verduurzaamwijzer.nu"],
                ["Telefoon", "06 4765 0537"],
              ]?.map(([label, value], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-muted/40" : ""}>
                  <td className="py-2 px-4 font-medium text-foreground/80 w-1/2 border border-border/40">{label}</td>
                  <td className="py-2 px-4 text-foreground/70 border border-border/40">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    title: "Onze achtergrond",
    content: (
      <div className="space-y-4">
        <p>
          Verduurzaamwijzer.nu is ontstaan uit frustratie met de ondoorzichtige markt voor woningverduurzaming.
          Huiseigenaren werden overspoeld met tegenstrijdige informatie, agressieve verkopers en onduidelijke
          subsidieregels. Wij besloten het anders te doen.
        </p>
        <p>
          Sinds onze oprichting hebben wij duizenden huiseigenaren geholpen bij het maken van een weloverwogen
          keuze. Wij vergelijken aanbieders op kwaliteit, prijs en service — niet op wie ons de hoogste commissie
          betaalt.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    title: "Wat wij doen",
    content: (
      <div className="space-y-4">
        <p>Verduurzaamwijzer.nu biedt:</p>
        <ul className="space-y-3 list-none">
          {[
            ["Onafhankelijk advies", "Wij geven advies op basis van uw woningsituatie, zonder commerciële bijbedoelingen."],
            ["Aanbiedersvergelijking", "Wij screenen en vergelijken gecertificeerde installateurs en leveranciers."],
            ["Kennisbank", "Uitgebreide, actuele informatie over alle verduurzamingsoplossingen."],
            ["Subsidie-overzicht", "Wij houden alle landelijke en regionale subsidies bij en maken ze inzichtelijk."],
            ["Offerteaanvraag", "Via ons platform vraagt u eenvoudig meerdere offertes aan bij betrouwbare aanbieders."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 4,
    title: "Ons team",
    content: (
      <div className="space-y-4">
        <p>
          Ons team bestaat uit specialisten met een achtergrond in energietechniek, bouwkunde, journalistiek en
          digitale productontwikkeling. Wij werken samen met gecertificeerde energieadviseurs (BRL 9500) en
          houden onze kennis continu up-to-date via brancheorganisaties zoals RVO, Milieu Centraal en Techniek
          Nederland.
        </p>
        <p>
          Wij geloven in transparantie, ook over onszelf. Lees meer over hoe wij werken op onze{' '}
          <Link to="/werkwijze" className="text-primary hover:underline font-medium">Werkwijze-pagina</Link>{' '}
          en hoe wij onze onafhankelijkheid waarborgen op de{' '}
          <Link to="/onafhankelijkheid" className="text-primary hover:underline font-medium">Onafhankelijkheid-pagina</Link>.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    title: "Contact opnemen",
    content: (
      <div className="space-y-3">
        <p>
          Heeft u vragen, suggesties of wilt u samenwerken? Wij horen graag van u.
        </p>
        <ul className="space-y-2 list-none">
          {[
            ["E-mail algemeen", "contact@verduurzaamwijzer.nu"],
            ["E-mail advies", "advies@verduurzaamwijzer.nu"],
            ["Telefoon", "06 4765 0537"],
            ["Adres", "Oude Middenweg 17, 2491 AC Den Haag"],
          ]?.map(([label, value], i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <span><strong>{label}:</strong> {value}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm text-foreground/70">
          Of gebruik ons{' '}
          <Link to="/contact" className="text-primary hover:underline font-medium">contactformulier</Link>{' '}
          voor een snelle reactie.
        </p>
      </div>
    ),
  },
];

const OverOnsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/8 via-background to-background pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-foreground/50 mb-4">
              <Link to="/homepage" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground/70">Over Ons</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">Over Ons</h1>
            </div>
            <p className="text-foreground/70 text-lg leading-relaxed max-w-2xl">
              Verduurzaamwijzer.nu helpt huiseigenaren met onafhankelijk advies over duurzame woningoplossingen.
              Leer ons kennen.
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-foreground/50">
              <span className="flex items-center gap-1.5">
                <Icon name="MapPin" size={14} />
                Den Haag, Nederland
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="Building2" size={14} />
                De Content Factory BV
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Sticky sidebar TOC */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Contact card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Neem contact op</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Vragen of opmerkingen? Bereik ons via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OverOnsPage;
